import { defineStore } from 'pinia'
import moment from 'moment'
import { flatten, camelCase } from 'lodash'
import repositoryFactory from '@/factories/repository-factory'
import SummaryRepository, { SummaryParams } from '@/repositories/summary-repository'
import Summary, { SummaryItemKey } from '@/models/Summary'
import { DefinitionInfoRainOrWaterItem, DefinitionInfoDefinitionSummary } from '@/models/initData/DefinitionInfo'
import { rangeToISO } from '@/lib/formatter'
import { useInitDataStore } from '@/stores/initDataStore'
import { windDirectionLabel } from '@/lib/constants'

const storeKey = 'summary'
const WIND_DIRECTION_FIELD_KEYS = ['moveWdMin10', 'moveMaxWdMin10', 'maxMoveMaxWdMin10', 'avgMoveWdMin10']
const RAIN_FIELD_KEYS = [
  'moveRainMin10',
  'moveRainMin60',
  'rainRui',
  'rainfall',
  'maxMoveRainMin10',
  'maxMoveRainMin60',
  'rainDay',
  'maxRainfall',
]

const formatDateTime = (datetime: string | undefined, format: string) => {
  if (!datetime) {
    return ''
  }

  if (moment(datetime).format('HHmm') === '0000') {
    return moment(datetime).add(-1, 'day').format(format).replaceAll('00:00', '24:00')
  } else {
    return moment(datetime).format(format)
  }
}

export const useSummaryStore = defineStore(storeKey, {
  state: () => ({
    data: [] as Summary[],
  }),

  getters: {
    find: state => (stationId: string) => {
      return state.data.find(s => s.stationId === stationId)
    },

    getRows:
      state =>
      (definitionInfo: DefinitionInfoRainOrWaterItem, info: DefinitionInfoDefinitionSummary, stationIds: string[]) => {
        const initDataStore = useInitDataStore()
        const key = camelCase(info.id) as SummaryItemKey

        return flatten(
          stationIds.map(stationId => {
            const item = definitionInfo.items.find(item => item.stationId === stationId)
            const datum = state.data.find(s => s.stationId === stationId)
            const station = initDataStore.findStation(stationId)

            if (!datum || !datum.items || !item) {
              return undefined
            }

            return item.summaryFieldMap[key].map(unit => {
              const lastKey = camelCase(unit)
              const tmpKey = lastKey.includes('Ap') ? lastKey.replace('Ap', '') : lastKey
              const target = datum.items[tmpKey]

              if (info.formatList && info.formatList[lastKey] && target) {
                const format = info.formatList[lastKey]
                const value = formatDateTime(target[key] as string, format)

                return { value }
              }

              if (target === undefined) {
                return { value: undefined }
              }

              let value = target[key] as number

              if (value !== undefined && lastKey.match(/Ap$/)) {
                value += station?.ap || 0
              }

              return { value, key: lastKey }
            })
          })
        )
      },

    getWeatherRows: state => (stationId: string, resolution: string) => {
      const initDataStore = useInitDataStore()
      const definition = initDataStore.initData.definitionInfo.reports.weather[resolution]
      const definitionSummary = definition.definitionSummary || []
      const rowKeys = definitionSummary.map(s => camelCase(s.id))
      const formats = {
        day: 'HH:mm',
        month: 'MM/DD HH:mm',
        year: 'MM/DD HH:mm',
      }
      const format = formats[resolution as keyof typeof formats]
      const rowNames = definitionSummary.map(s => s.title)
      const data = state.data.find(d => d.stationId === stationId)
      const items = data?.items
      const summaryFieldMap = definition.items[0].summaryFieldMap

      return rowKeys.map((key, index) => {
        const values = summaryFieldMap[key].map(f => {
          if (items === undefined || items[camelCase(f)] === undefined) {
            return ''
          }

          const item = items[camelCase(f)]
          const result = item[key as SummaryItemKey]

          if (result === undefined || result === null || result === '') {
            return ''
          }

          if (WIND_DIRECTION_FIELD_KEYS.includes(camelCase(f)) && !['maxTime', 'minTime'].includes(key)) {
            return windDirectionLabel(Number(result)) || ''
          }

          // 雨量系のフィールドで最低値が0のときは起時を非表示にさせる
          const isRainKey = RAIN_FIELD_KEYS.includes(camelCase(f))
          if (key === 'minTime' && isRainKey && item?.min === 0) {
            return ''
          }

          // key=日数のときだけ整数値で出力
          if (key === 'count') {
            return result
          }

          return ['maxTime', 'minTime'].includes(key) ? moment(result).format(format) : (result as number).toFixed(1)
        })

        return { title: rowNames[index], values }
      })
    },

    getWdCountRate: state => (stationId: string, resolution: string) => {
      const data = state.data.find(d => d.stationId === stationId)
      const key = resolution === 'year' ? 'moveWdMin10OfMonthCountRate' : 'moveWdMin10CountRate'

      return Object.values(data?.items[key]?.sum || {})
    },
  },

  actions: {
    async fetch(params: SummaryParams) {
      const repository = (await repositoryFactory.create()).get(storeKey) as SummaryRepository

      return repository.get(params)
    },

    async fetchAll(stationIds: string[], type: string, range: Range, target: string[]) {
      const promises = [] as Promise<unknown>[]
      const results = [] as Summary[]
      const targetForAp = [
        ...target,
        ...target.map(t => (t.includes('_ap') ? t.replace('_ap', '') : '')).filter(t => t),
      ]

      stationIds.forEach(stationId => {
        const params = {
          stationId,
          range: {
            ...rangeToISO(range),
            type,
          },
          target: targetForAp,
        }

        const promise = this.fetch(params).then(summary => {
          results.push({ ...summary, stationId })
        })

        promises.push(promise)
      })

      await Promise.all(promises)

      this.data = results
    },

    async fetchWindCount(stationId: string, range: Range, type: string) {
      const params = {
        stationId,
        range: {
          ...range,
          type,
        },
        target: ['move_wd_min_10_count_rate', 'move_wd_min_10_of_month_count_rate'],
      }
      const repository = (await repositoryFactory.create()).get(storeKey) as SummaryRepository

      this.data = [await repository.get(params)]
    },
  },
})
